import { z } from 'zod'
import { FirestoreTimestamp } from './utils'

// Individual repair item schemas
const ItemSchema = z.object({
  description: z.string().min(1, 'Repair description is required'),
  comments: z.string().optional(),
  photos: z.array(z.string()).default([]),
})

// Base repair schema with all fields
const BaseRepairSchema = z.object({
  // Core repair data
  date: z.iso.date(),
  repairs: z.array(ItemSchema).default([]),
  replacements: z.array(ItemSchema).default([]),
  alterations: z.array(ItemSchema).default([]),

  // Technician information
  completedBy: z.string().min(1, 'Completed by is required'),
  completedByUserId: z.string().optional(), // Optional link to actual user document
})

// Full repair schema (for database documents)
export const Repair = BaseRepairSchema.extend({
  unitId: z.string(),
  createdAt: FirestoreTimestamp,
  updatedAt: FirestoreTimestamp,
})

// Create repair schema - requires all fields except auto-generated ones
export const CreateRepairSchema = BaseRepairSchema.extend({
  // Required fields for creation
  unitId: z.string().min(1, 'Unit ID is required'),
  siteId: z.string().min(1, 'Site ID is required'),
  date: z.iso.date(),
}).partial({
  // Make optional fields explicitly partial
  completedBy: true,
  completedByUserId: true,
})

// Update repair schema - only siteId, unitId, and repairId are required
export const UpdateRepairSchema = CreateRepairSchema.extend({
  repairId: z.string().min(1, 'Repair ID is required'),
}).partial({
  // Make all data fields optional for updates, keeping only identifiers required
  date: true,
  repairs: true,
  replacements: true,
  alterations: true,
  completedBy: true,
})

export type Repair = z.infer<typeof Repair>
export type CreateRepairData = z.infer<typeof CreateRepairSchema>
export type UpdateRepairData = z.infer<typeof UpdateRepairSchema>
